import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  TestsPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
} from "./TestsPage.styles";
import Sidebar from "components/Sidebar";
import Table from "components/Table";
import IconButton from "components/Buttons/IconButton";
import { Test } from "services/documentService/documentService.types";
import { fetchTests, createTest, updateTest, deleteTest } from "services/documentService/documentService";
import Button from "components/Buttons/Button";
import { ButtonWrapper } from "../TemplatesPage.styles";
import { ConfirmModal, CreateModal } from "components/Modals";
import DeletedRow from "components/DeletedRow";
import RestoredRow from "components/RestoredRow";
import Breadcrumbs from "components/Breadcrumbs/Breadcrumbs";
import { ButtonsContainer } from "../../DocumentVersionsPage/DocumentVersionsPage.styles";
import { formatServerErrors } from "utils/errorHandler";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";
import { getServerErrorData } from "utils/httpError";
import { delIcon, editIcon } from "assets/icons";
const TestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [testToEdit, setTestToEdit] = useState<Test | null>(null);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const toast = useNotify();

  // Состояния для подтверждения удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [testToDelete, setTestToDelete] = useState<Test | null>(null);

  // Состояния для реализации отложенного удаления и уведомлений
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [searchParams] = useSearchParams();
  
    const documentName = searchParams.get("documentName") || "Неизвестный документ";
    const templateName = searchParams.get("templateName") || "Неизвестный шаблон";
    const { documentId } = useParams<{ documentId: string }>();
    const templateId = searchParams.get("templateId") || "0";

  const {
    items: tests,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    reload: loadTests,
  } = useListPage<Test, Awaited<ReturnType<typeof fetchTests>>>({
    fetcher: ({ page, pageSize: size, orderBy }) =>
      fetchTests(Number(documentId), page, size, orderBy),
    select: (response) => ({ items: response.results, total: response.count }),
    errorMessage: "Ошибка загрузки тестов",
  });

  useEffect(() => {
    if (!templateId) {
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Необходимо указать документ.",
      });
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const openDeleteModal = (test: Test) => {
    setTestToDelete(test);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTestToDelete(null);
  };

  const confirmDelete = () => {
    if (testToDelete) {
      handleDelete(testToDelete.id);
      closeDeleteModal();
    }
  };

  // Отложенное удаление с задержкой в 5 секунд
  const handleDelete = (id: number) => {
    setPendingDelete(id);
    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        await deleteTest(id);
        setPendingDelete(null);
        toast.show({
          severity: "success",
          summary: "Удалено",
          detail: "Тест успешно удален",
          life: 2000,
        });
        loadTests();
      } catch (error) {
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось удалить тест",
          life: 3000,
        });
        logger.error("Ошибка при удалении теста:", error);
        setPendingDelete(null);
      }
    }, 5000);
  };

  // Функция восстановления – теперь устанавливаем recentlyRestored
  const restoreTestHandler = () => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    const restoredId = pendingDelete; // восстанавливаем именно тот, который был отложен
    setPendingDelete(null);
    setRecentlyRestored(restoredId);
    toast.show({
      severity: "success",
      summary: "Восстановлено",
      detail: "Удаление теста отменено",
      life: 3000,
    });
    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  const openCreateModal = () => {
    setTestToEdit(null);
    setIsCreateModalOpen(true);
  };

  const openEditModal = (test: Test) => {
    setTestToEdit(test);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateOrEditTest = async (formData: Record<string, string | File[] | null>) => {
    setExternalErrors({});
    try {
      if (testToEdit) {
        await updateTest(testToEdit.id, { name: formData["name"] as string, content: formData["content"] as string, version: documentId  });
        setRecentlyEdited(testToEdit.id);
        toast.show({
          severity: "success",
          summary: "Успех",
          detail: "Тест обновлен",
          life: 3000,
        });
      } else {
        logger.log("documentId", documentId);
        const createdTest = await createTest(Number(documentId), { name: formData["name"] as string, content: formData["content"] as string, version: documentId  });
        setRecentlyCreated(createdTest.id);
        toast.show({
          severity: "success",
          summary: "Успех",
          detail: "Тест создан",
          life: 3000,
        });
      }
      closeCreateModal();
      loadTests();
      setTimeout(() => {
        setRecentlyCreated(null);
        setRecentlyEdited(null);
      }, 3000);
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors = formatServerErrors(serverErrors);
        setExternalErrors(formattedErrors);
      } else {
        logger.error("Ошибка при сохранении теста:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Ошибка сохранения теста",
          life: 3000,
        });
      }
    }
  };

  const headers = [
    { label: "Название теста", key: "name", fieldName: "name" },
    { label: "Содержание", key: "view", sortable: false },
    { label: "", key: "buttons", sortable: false, fieldName: "delete" },
  ];

  const rows = tests.map((test) => {
    if (test.id === pendingDelete) {
      return {
        name: (
          <DeletedRow
            id={test.id}
            onRestore={restoreTestHandler}
            colSpan={headers.length}
            message={`Тест "${test.name}" будет удален через 5 секунд.`}
            targetText="Отменить удаление"
          />
        ),
      };
    }
    return {
      name: test.name,
      view: (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "300px",
          }}
          title={test.content}
        >
          {test.content}
        </div>
      ),
      buttons: (
        <ButtonsContainer>
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => openEditModal(test)}
          />
          <IconButton
            icon={delIcon}
            tone="danger"
            altText="Удалить"
            onClick={() => openDeleteModal(test)}
          />
        </ButtonsContainer>
      ),
    };
  });

  // Вставляем строки уведомлений, как в CoursesPage
  if (recentlyRestored !== null) {
    const restoredIndex = tests.findIndex((t) => t.id === recentlyRestored);
    if (restoredIndex !== -1) {
      rows.splice(restoredIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Тест успешно восстановлен" />
        ),
      });
    }
  }
  if (recentlyCreated !== null) {
    const createdIndex = tests.findIndex((t) => t.id === recentlyCreated);
    if (createdIndex !== -1) {
      rows.splice(createdIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Тест успешно создан" />
        ),
      });
    }
  }
  if (recentlyEdited !== null) {
    const editedIndex = tests.findIndex((t) => t.id === recentlyEdited);
    if (editedIndex !== -1) {
      rows.splice(editedIndex + 1, 0, {
        name: (
          <RestoredRow colSpan={headers.length} message="Тест успешно отредактирован" />
        ),
      });
    }
  }

  const rowAmountOptions = [
    { label: "7 записей", value: 7 },
    { label: "15 записей", value: 15 },
    { label: "20 записей", value: 20 },
  ];

  return (
    <TestsPageContainer>
      <Sidebar />
      <ContentWrapper>
        <Breadcrumbs
          items={[
            { label: "Шаблоны документов", to: "/documents" },
            {
              label: "Шаблон " + templateName,
              to: "/documents/" + templateId + "/versions?templateName=" + templateName,
            },
            { label: "Тесты документа " + documentName },
          ]}
        />
        <HeaderContainer>
          <Title>Тесты</Title>
          <ButtonWrapper>
            <Button onClick={openCreateModal} variant="primary" size="lg">
              Добавить тест
            </Button>
          </ButtonWrapper>
        </HeaderContainer>
        <Table
          emptyState={{
            title: "Тестов пока нет",
            description:
              "Добавьте тест — он появится в этом списке.",
          }}
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowAmountOptions={rowAmountOptions}
          loading={loading}
          minHeight="200px"
          onSort={handleSort}
          specialRowKey="name"
        />
      </ContentWrapper>
      {isCreateModalOpen && (
        <CreateModal
          title={testToEdit ? "Редактировать тест" : "Новый тест"}
          fields={[
            { name: "name", type: "text", placeholder: "Название теста", required: true },
            { name: "content", type: "textarea", placeholder: "Содержание", required: true },
          ]}
          onSubmit={handleCreateOrEditTest}
          submitButtonText={testToEdit ? "Сохранить" : "Создать"}
          onCancel={closeCreateModal}
          initialValues={testToEdit ? { name: testToEdit.name, content: testToEdit.content } : {}}
          externalErrors={externalErrors}
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Подтверждение удаления"
          message={`Вы уверены, что хотите удалить тест "${testToDelete?.name}"?`}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          confirmButtonText="Удалить"
        />
      )}
    </TestsPageContainer>
  );
};

export default TestsPage;
